import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, VStack, HStack, Text, Heading, Badge, Button,
  Divider, Spinner, Alert, AlertIcon, Container, Flex, Icon,
  SimpleGrid, Table, Thead, Tbody, Tr, Th, Td, Checkbox, Select,
  Modal, ModalOverlay, ModalContent, ModalBody, useDisclosure, useToast,
  Grid, GridItem,
} from '@chakra-ui/react';
import {
  FaCreditCard, FaCheckCircle, FaLock, FaPlus, FaTimes,
  FaFileInvoiceDollar, FaWallet, FaRegCreditCard,
  FaVial, FaStethoscope, FaSyringe, FaEye, FaPrint,
} from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Save card form (SetupIntent)
function SaveCardForm({ onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);

    const { error: setupError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (setupError) {
      setError(setupError.message);
      setSaving(false);
    } else if (setupIntent && setupIntent.status === 'succeeded') {
      onSuccess();
    } else {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <PaymentElement options={{ layout: 'tabs' }} />
        {error && (
          <Alert status="error" borderRadius="md" fontSize="sm">
            <AlertIcon />{error}
          </Alert>
        )}
        <Button
          type="submit"
          bgGradient="linear(to-r, #276749, #38a169)"
          color="white" size="lg" width="full"
          leftIcon={<Icon as={FaPlus} />}
          isLoading={saving || !stripe}
          loadingText="Saving card..."
          fontSize="md" py={7} borderRadius="xl"
          _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
          _active={{ transform: 'translateY(0)' }}
          transition="all 0.2s" boxShadow="md"
          isDisabled={!stripe || !elements}
        >
          Save Card
        </Button>
        <HStack spacing={1.5} justify="center" pb={1}>
          <Icon as={FaLock} color="gray.400" boxSize={3} />
          <Text fontSize="xs" color="gray.400">Your card is securely saved via Stripe</Text>
        </HStack>
      </VStack>
    </form>
  );
}

// Pay with new card form (PaymentIntent)
function PayCardForm({ amount, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    setError(null);

    const { error: payError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (payError) {
      setError(payError.message);
      setPaying(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess();
    } else {
      setPaying(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <PaymentElement options={{ layout: 'tabs' }} />
        {error && (
          <Alert status="error" borderRadius="md" fontSize="sm">
            <AlertIcon />{error}
          </Alert>
        )}
        <Button
          type="submit"
          bgGradient="linear(to-r, #276749, #38a169)"
          color="white" size="lg" width="full"
          leftIcon={<Icon as={FaCreditCard} />}
          isLoading={paying || !stripe}
          loadingText="Processing..."
          fontSize="md" py={7} borderRadius="xl"
          _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
          _active={{ transform: 'translateY(0)' }}
          transition="all 0.2s" boxShadow="md"
          isDisabled={!stripe || !elements}
        >
          Pay {formatCurrency(amount)}
        </Button>
        <HStack spacing={1.5} justify="center" pb={1}>
          <Icon as={FaLock} color="gray.400" boxSize={3} />
          <Text fontSize="xs" color="gray.400">Secure payment powered by Stripe</Text>
        </HStack>
      </VStack>
    </form>
  );
}

export default function LabInvoicesPage() {
  const { labId } = useParams();
  const [searchParams] = useSearchParams();
  const secretKey = searchParams.get('key');

  const [lab, setLab] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [savedCards, setSavedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // Modal states
  const { isOpen: isAddCardOpen, onOpen: onAddCardOpen, onClose: onAddCardClose } = useDisclosure();
  const { isOpen: isPayOpen, onOpen: onPayOpen, onClose: onPayClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();

  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [payClientSecret, setPayClientSecret] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [chargingCard, setChargingCard] = useState(null);
  const [selectedCard, setSelectedCard] = useState('');
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [viewingDetail, setViewingDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const redactPHI = true;
  const [printHtml, setPrintHtml] = useState(null);
  const { isOpen: isPrintOpen, onOpen: onPrintOpen, onClose: onPrintClose } = useDisclosure();
  const toast = useToast();

  const apiBase = process.env.REACT_APP_API_URL || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = secretKey ? `secret_key=${secretKey}` : `lab_id=${labId}`;
      const res = await fetch(`${apiBase}/api/lab-invoices?${params}`);
      if (!res.ok) throw new Error('Failed to load invoices');
      const data = await res.json();
      setLab(data.lab);
      setInvoices(data.invoices || []);

      // Fetch saved cards
      if (data.lab?.id) {
        const cardsRes = await fetch(`${apiBase}/api/lab-payment-methods?lab_id=${data.lab.id}${secretKey ? `&secret_key=${encodeURIComponent(secretKey)}` : ''}`);
        if (cardsRes.ok) {
          const cardsData = await cardsRes.json();
          const cards = cardsData.payment_methods || [];
          setSavedCards(cards);
          if (cards.length > 0 && !selectedCard) setSelectedCard(cards[0].id);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [labId, secretKey, apiBase, selectedCard]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const unpaidInvoices = invoices.filter(inv => inv.payment_status !== 'paid');
  const paidInvoices = invoices.filter(inv => inv.payment_status === 'paid');
  const selectedTotal = selectedInvoices.reduce((sum, id) => {
    const inv = invoices.find(i => i.id === id);
    return sum + (inv?.total_amount || 0);
  }, 0);

  const toggleInvoice = (id) => {
    setSelectedInvoices(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllUnpaid = () => {
    if (selectedInvoices.length === unpaidInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(unpaidInvoices.map(i => i.id));
    }
  };

  // Add Card flow
  const handleAddCard = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/create-setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lab_id: lab.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Failed');
      setSetupClientSecret(data.clientSecret);
      onAddCardOpen();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCardSaved = () => {
    onAddCardClose();
    setSetupClientSecret(null);
    toast({ title: 'Card saved!', description: 'Your card has been saved on file.', status: 'success', duration: 3000 });
    fetchData();
  };

  // Pay with new card flow
  const handlePayNewCard = async () => {
    if (selectedInvoices.length === 0) {
      toast({ title: 'Select invoices', description: 'Please select invoices to pay.', status: 'warning', duration: 3000 });
      return;
    }
    setActionLoading(true);
    try {
      const tokens = selectedInvoices.map(id => {
        const inv = invoices.find(i => i.id === id);
        return inv?.public_token;
      }).filter(Boolean);
      const res = await fetch(`${apiBase}/api/create-invoice-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokens }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Failed');
      setPayClientSecret(data.clientSecret);
      onPayOpen();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    onPayClose();
    setPayClientSecret(null);
    setSelectedInvoices([]);
    toast({ title: 'Payment successful!', description: 'Your invoices have been paid.', status: 'success', duration: 3000 });
    setTimeout(() => fetchData(), 2000);
  };

  // PHI helpers
  const getLastName = (fullName) => {
    if (!fullName) return '—';
    const parts = fullName.trim().split(' ');
    return parts[parts.length - 1];
  };
  const getBirthYear = (dobStr) => {
    if (!dobStr) return '—';
    const d = new Date(dobStr);
    return isNaN(d.getTime()) ? '—' : d.getFullYear().toString();
  };
  const redact = (text) => redactPHI ? '••••••••' : text;

  // Open invoice detail overlay
  const openInvoiceDetail = async (inv) => {
    setViewingInvoice(inv);
    setViewingDetail(null);
    onViewOpen();
    setDetailLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/invoice-detail?invoice_id=${inv.id}&lab_id=${lab.id}`);
      if (res.ok) {
        const data = await res.json();
        setViewingDetail(data);
      }
    } catch (err) {
      // Silently fail — modal will show "no details" state
    } finally {
      setDetailLoading(false);
    }
  };

  const isViewingPaid = viewingInvoice?.payment_status === 'paid';

  // Build invoice HTML for printing
  const buildInvoiceHtml = (inv, detail) => {
    if (!detail?.line_items?.length) return '';
    const isPaid = inv.payment_status === 'paid';
    const totalDraw = detail.line_items.reduce((s, i) => s + (i.draw_fee || 0), 0);
    const totalAdmin = detail.line_items.reduce((s, i) => s + (i.admin_fee || 0), 0);
    const totalShip = detail.line_items.reduce((s, i) => s + (i.ship_fee || 0), 0);
    const totalAmount = inv.total_amount || (totalDraw + totalAdmin + totalShip);
    return `
    <div class="invoice-page">
      <div class="header">
        <div class="header-flex">
          <div>
            <div class="brand">AtHomeBloodDraw.com</div>
            <div class="title">INVOICE</div>
            <div class="subtitle">Mobile Phlebotomy Services</div>
          </div>
          <div style="text-align:right">
            <div class="inv-label">Invoice No.</div>
            <div class="inv-number">${inv.invoice_number}</div>
            <div class="inv-date">Issued ${formatDate(inv.created_at)}</div>
          </div>
        </div>
      </div>
      <div class="status-bar ${isPaid ? 'paid' : 'unpaid'}">
        <span class="status-text ${isPaid ? 'paid' : 'unpaid'}">${isPaid ? '&#10004; Payment Received' : '&#9888; Payment Required'}</span>
        <span class="status-amount ${isPaid ? 'paid' : 'unpaid'}">${formatCurrency(totalAmount)}</span>
      </div>
      <div class="bill-to">
        <div>
          <div class="bill-label">Bill To</div>
          <div class="bill-name">${lab?.name || 'Lab'}</div>
          <div class="bill-email">${lab?.email || ''}</div>
        </div>
        <div style="text-align:right">
          <div class="bill-label">Status</div>
          <span class="badge ${isPaid ? 'paid' : 'unpaid'}">${isPaid ? 'PAID' : 'UNPAID'}</span>
        </div>
      </div>
      <hr class="divider">
      <div class="items">
        <div class="items-header">
          <span class="col-detail">Service Details</span>
          <span class="col-date">Collection Date</span>
          <span class="col-amount">Amount</span>
        </div>
        ${detail.line_items.map(item => {
          const itemTotal = (item.draw_fee || 0) + (item.admin_fee || 0) + (item.ship_fee || 0);
          return `<div class="item">
            <div class="col-detail">
              <div class="patient-name">${getLastName(item.patient_name)}</div>
              <div class="meta">YOB: ${getBirthYear(item.patient_dob)}</div>
              ${item.doctor_name || item.phlebotomist_name ? `<div class="meta">${item.doctor_name ? `Dr: ${item.doctor_name}` : ''}${item.doctor_name && item.phlebotomist_name ? ' &nbsp;|&nbsp; ' : ''}${item.phlebotomist_name ? `Phleb: ${item.phlebotomist_name}` : ''}</div>` : ''}
              ${item.tests && item.tests.length > 0 ? `<div class="tests">${item.tests.map(t => `<span class="test-badge">${t}</span>`).join('')}</div>` : ''}
              ${item.diagnosis ? `<div class="icd${redactPHI ? ' redacted' : ''}">ICD: ${redact(item.diagnosis)}</div>` : ''}
            </div>
            <div class="col-date"><span class="item-date">${formatDate(item.collection_date)}</span></div>
            <div class="col-amount item-amount">
              <span class="item-total">${formatCurrency(itemTotal)}</span>
              <span class="item-breakdown">Draw $${(item.draw_fee||0).toFixed(2)} + Admin $${(item.admin_fee||0).toFixed(2)}${item.ship_fee > 0 ? ` + Ship $${(item.ship_fee||0).toFixed(2)}` : ''}</span>
            </div>
          </div>`;
        }).join('')}
      </div>
      <div class="summary">
        <div>
          <div class="summary-label">Total Due</div>
          <div class="summary-draws">${detail.line_items.length} draws</div>
        </div>
        <div class="summary-total ${isPaid ? 'paid' : ''}">${formatCurrency(totalAmount)}</div>
      </div>
      <div class="fees">
        <div><div class="fee-label">Draw Fees</div><div class="fee-value">${formatCurrency(totalDraw)}</div></div>
        <div><div class="fee-label">Admin Fees</div><div class="fee-value">${formatCurrency(totalAdmin)}</div></div>
        <div><div class="fee-label">Shipping</div><div class="fee-value">${formatCurrency(totalShip)}</div></div>
      </div>
      <div class="footer">
        <span style="font-weight:600">AtHomeBloodDraw.com</span>
        <span>Questions? Contact support@athomeblooddraw.com</span>
      </div>
    </div>`;
  };

  const printStyles = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #333; }
    .invoice-page { page-break-after: always; }
    .invoice-page:last-child { page-break-after: avoid; }
    .header { background: linear-gradient(to right, #276749, #38a169); color: white; padding: 40px; }
    .header-flex { display: flex; justify-content: space-between; align-items: flex-start; }
    .brand { font-size: 16px; font-weight: 800; letter-spacing: 0.5px; }
    .title { font-size: 32px; font-weight: 800; margin-top: 4px; }
    .subtitle { font-size: 12px; opacity: 0.7; margin-top: 4px; }
    .inv-label { font-size: 10px; opacity: 0.6; text-transform: uppercase; letter-spacing: 2px; }
    .inv-number { font-size: 22px; font-weight: 800; letter-spacing: 1px; margin-top: 2px; }
    .inv-date { font-size: 11px; opacity: 0.6; margin-top: 4px; }
    .status-bar { padding: 12px 40px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
    .status-bar.paid { background: #f0fff4; border-color: #c6f6d5; }
    .status-bar.unpaid { background: #fffaf0; border-color: #feebc8; }
    .status-text { font-size: 13px; font-weight: 700; }
    .status-text.paid { color: #276749; }
    .status-text.unpaid { color: #c05621; }
    .status-amount { font-size: 20px; font-weight: 800; }
    .status-amount.paid { color: #276749; }
    .status-amount.unpaid { color: #c05621; }
    .bill-to { padding: 24px 40px; display: flex; justify-content: space-between; }
    .bill-label { font-size: 10px; color: #999; text-transform: uppercase; font-weight: 700; letter-spacing: 1px; margin-bottom: 4px; }
    .bill-name { font-size: 16px; font-weight: 700; }
    .bill-email { font-size: 13px; color: #777; margin-top: 2px; }
    .badge { display: inline-block; padding: 4px 14px; border-radius: 999px; font-size: 12px; font-weight: 700; }
    .badge.paid { background: #c6f6d5; color: #276749; }
    .badge.unpaid { background: #fed7d7; color: #c53030; }
    .divider { border: none; border-top: 1px solid #eee; }
    .items { padding: 20px 40px; }
    .items-header { display: flex; padding-bottom: 12px; margin-bottom: 16px; border-bottom: 2px solid #e2e8f0; }
    .items-header span { font-size: 10px; font-weight: 800; color: #999; text-transform: uppercase; letter-spacing: 1px; }
    .col-detail { flex: 1; }
    .col-date { width: 120px; }
    .col-amount { width: 90px; text-align: right; }
    .item { display: flex; padding: 14px 0; border-bottom: 1px solid #f0f0f0; }
    .item:last-child { border-bottom: none; }
    .patient-name { font-size: 13px; font-weight: 700; }
    .meta { font-size: 11px; color: #777; margin-top: 3px; }
    .tests { margin-top: 6px; }
    .test-badge { display: inline-block; background: #f0fff4; color: #276749; border: 1px solid #c6f6d5; font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 999px; margin-right: 4px; margin-bottom: 2px; }
    .icd { font-size: 11px; color: #999; margin-top: 4px; }
    .redacted { filter: blur(5px); user-select: none; -webkit-user-select: none; pointer-events: none; }
    .item-date { font-size: 13px; color: #555; display: flex; align-items: center; }
    .item-amount { text-align: right; display: flex; flex-direction: column; justify-content: center; }
    .item-total { font-size: 13px; font-weight: 700; }
    .item-breakdown { font-size: 9px; color: #999; }
    .summary { background: #f7fafc; padding: 16px 40px; border-top: 2px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
    .summary-label { font-size: 10px; color: #999; text-transform: uppercase; font-weight: 700; }
    .summary-draws { font-size: 13px; color: #777; }
    .summary-total { font-size: 24px; font-weight: 800; }
    .summary-total.paid { color: #276749; }
    .fees { padding: 16px 40px; border-top: 1px solid #eee; display: flex; gap: 40px; }
    .fee-label { font-size: 10px; color: #999; font-weight: 700; }
    .fee-value { font-size: 13px; font-weight: 600; color: #555; }
    .footer { padding: 20px 40px; display: flex; justify-content: space-between; font-size: 11px; color: #999; margin-top: 20px; }
    @media print {
      body { padding: 0; }
      .header, .status-bar, .summary, .test-badge, .badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  // Build full HTML document for print preview
  const buildPrintDoc = (bodyHtml, title) =>
    `<!DOCTYPE html><html><head><title>${title} - AtHomeBloodDraw.com</title><style>${printStyles}</style></head><body>${bodyHtml}</body></html>`;

  // Print single invoice (needs detail data — fetch then show overlay)
  const handlePrintInvoice = async (inv) => {
    try {
      const res = await fetch(`${apiBase}/api/invoice-detail?invoice_id=${inv.id}&lab_id=${lab.id}`);
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      const html = buildInvoiceHtml(inv, data);
      if (!html) return;
      setPrintHtml(buildPrintDoc(html, inv.invoice_number));
      onPrintOpen();
    } catch (err) {
      toast({ title: 'Error', description: 'Could not load invoice details for printing.', status: 'error', duration: 3000 });
    }
  };

  // Print from the already-loaded detail modal
  const handlePrintFromModal = () => {
    if (!viewingInvoice || !viewingDetail) return;
    const html = buildInvoiceHtml(viewingInvoice, viewingDetail);
    if (!html) return;
    setPrintHtml(buildPrintDoc(html, viewingInvoice.invoice_number));
    onPrintOpen();
  };

  // Trigger browser print from the iframe
  const handlePrintFromOverlay = () => {
    const iframe = document.getElementById('print-preview-iframe');
    if (iframe?.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  // Charge saved card
  const handleChargeSavedCard = async (cardId) => {
    if (selectedInvoices.length === 0) {
      toast({ title: 'Select invoices', description: 'Please select invoices to pay.', status: 'warning', duration: 3000 });
      return;
    }
    setChargingCard(cardId);
    try {
      const res = await fetch(`${apiBase}/api/charge-saved-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lab_id: lab.id,
          payment_method_id: cardId,
          invoice_ids: selectedInvoices,
          secret_key: secretKey || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Payment failed');

      // Handle 3D Secure authentication if required
      if (data.requires_action && data.client_secret) {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Stripe not loaded');
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(data.client_secret);
        if (confirmError) throw new Error(confirmError.message);
        if (paymentIntent?.status !== 'succeeded') throw new Error('Payment was not completed');
        // 3DS succeeded — confirm payment on backend to mark invoices as paid
        let confirmOk = false;
        try {
          const confirmRes = await fetch(`${apiBase}/api/confirm-card-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              payment_intent_id: data.payment_intent_id,
              invoice_ids: data.invoice_ids,
              secret_key: secretKey || null,
              lab_id: lab.id,
            }),
          });
          confirmOk = confirmRes.ok;
        } catch (_) { /* webhook will handle it as fallback */ }

        if (!confirmOk) {
          // Payment went through Stripe but couldn't confirm in our system — show warning
          setSelectedInvoices([]);
          toast({ title: 'Payment received', description: 'Your payment was processed but may take a moment to update. Please refresh in a few seconds.', status: 'warning', duration: 8000 });
          setTimeout(() => fetchData(), 3000);
          return;
        }
      }

      setSelectedInvoices([]);
      toast({ title: 'Payment successful!', description: `Charged ${formatCurrency(data.amount)} to your saved card.`, status: 'success', duration: 4000 });
      setTimeout(() => fetchData(), 1500);
    } catch (err) {
      toast({ title: 'Payment failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setChargingCard(null);
    }
  };

  const stripeAppearance = {
    theme: 'stripe',
    variables: { colorPrimary: '#38a169', borderRadius: '8px' },
  };

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="green.500" thickness="4px" />
          <Text color="gray.500" fontSize="sm">Loading invoices...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error && !lab) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <Box bg="white" p={10} borderRadius="2xl" boxShadow="xl" maxW="md" w="full" textAlign="center">
          <Icon as={FaFileInvoiceDollar} boxSize={14} color="gray.200" mb={4} />
          <Heading size="md" mb={2} color="gray.700">Unable to Load</Heading>
          <Text color="gray.500" fontSize="sm">{error}</Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 4, md: 8 }}>
      <Container maxW="900px" px={{ base: 3, md: 6 }}>

        {/* Header Card */}
        <Box bg="white" borderRadius="2xl" boxShadow="xl" overflow="hidden" border="1px solid" borderColor="gray.100" mb={6}>
          <Box bgGradient="linear(to-r, #276749, #38a169)" color="white" px={{ base: 6, md: 10 }} py={8} position="relative" overflow="hidden">
            <Box position="absolute" top={0} right={0} bottom={0} left={0} opacity={0.05}
              bgImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)" bgSize="20px 20px" />
            <Flex justify="space-between" align="flex-start" position="relative">
              <VStack align="start" spacing={2}>
                <Text fontSize="lg" fontWeight="800" letterSpacing="0.5px">AtHomeBloodDraw.com</Text>
                <Heading size="xl" fontWeight="800" letterSpacing="-0.5px">INVOICES</Heading>
                <Text fontSize="sm" opacity={0.7}>Mobile Phlebotomy Services</Text>
              </VStack>
              <VStack align="end" spacing={1}>
                <Text fontSize="md" fontWeight="700">{lab?.name || 'Lab'}</Text>
                <Text fontSize="xs" opacity={0.7}>{invoices.length} total invoices</Text>
              </VStack>
            </Flex>
          </Box>

          {/* Summary Bar */}
          <Box px={{ base: 6, md: 10 }} py={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">Unpaid</Text>
                <Text fontSize="xl" fontWeight="800" color="orange.600">{unpaidInvoices.length}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">Unpaid Total</Text>
                <Text fontSize="xl" fontWeight="800" color="orange.600">
                  {formatCurrency(unpaidInvoices.reduce((s, i) => s + i.total_amount, 0))}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">Paid</Text>
                <Text fontSize="xl" fontWeight="800" color="green.600">{paidInvoices.length}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">Cards on File</Text>
                <Text fontSize="xl" fontWeight="800" color="gray.700">{savedCards.length}</Text>
              </Box>
            </SimpleGrid>
          </Box>
        </Box>

        {/* Card on File & Charge Section */}
        <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
          <Box px={{ base: 4, md: 6 }} py={4} bg="blue.50" borderBottom="1px solid" borderColor="blue.100">
            <HStack spacing={2}>
              <Icon as={FaWallet} color="blue.500" />
              <Text fontSize="md" fontWeight="700" color="blue.700">Card on File</Text>
            </HStack>
          </Box>
          <Box px={{ base: 4, md: 6 }} py={5}>
            {savedCards.length > 0 ? (
              <VStack spacing={4} align="stretch">
                <Select
                  value={selectedCard}
                  onChange={(e) => setSelectedCard(e.target.value)}
                  size="md" borderRadius="lg"
                  icon={<FaRegCreditCard />}
                >
                  {savedCards.map(card => (
                    <option key={card.id} value={card.id}>
                      {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} •••• {card.last4} — Exp {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                    </option>
                  ))}
                </Select>
                <HStack spacing={3}>
                  <Button
                    colorScheme="green" size="md" flex={1}
                    leftIcon={<FaCreditCard />}
                    onClick={() => handleChargeSavedCard(selectedCard)}
                    isLoading={chargingCard === selectedCard}
                    isDisabled={selectedInvoices.length === 0 || !selectedCard}
                  >
                    Charge Selected ({selectedInvoices.length}) — {formatCurrency(selectedTotal)}
                  </Button>
                  <Button
                    size="md" variant="outline" colorScheme="green"
                    leftIcon={<FaPlus />}
                    onClick={handleAddCard}
                  >
                    Add Card
                  </Button>
                </HStack>
                {selectedInvoices.length === 0 && (
                  <Text fontSize="xs" color="gray.400" textAlign="center">Select invoices below to charge this card</Text>
                )}
              </VStack>
            ) : (
              <VStack spacing={3}>
                <Text fontSize="sm" color="gray.500">No cards on file yet.</Text>
                <Button
                  size="md" colorScheme="green" variant="outline"
                  leftIcon={<FaPlus />}
                  onClick={handleAddCard}
                >
                  Add Card on File
                </Button>
              </VStack>
            )}
          </Box>
        </Box>

        {/* Unpaid Invoices */}
        {unpaidInvoices.length > 0 && (
          <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
            <Box px={{ base: 4, md: 6 }} py={4} bg="orange.50" borderBottom="1px solid" borderColor="orange.100">
              <Flex justify="space-between" align="center">
                <HStack spacing={2}>
                  <Icon as={FaCreditCard} color="orange.500" />
                  <Text fontSize="md" fontWeight="700" color="orange.700">Unpaid Invoices</Text>
                </HStack>
                {selectedInvoices.length > 0 && (
                  <HStack spacing={3}>
                    <Text fontSize="sm" fontWeight="600" color="orange.700">
                      {selectedInvoices.length} selected — {formatCurrency(selectedTotal)}
                    </Text>
                    <Button
                      size="sm" colorScheme="green"
                      leftIcon={<FaCreditCard />}
                      onClick={handlePayNewCard}
                      isLoading={actionLoading}
                    >
                      Pay with New Card
                    </Button>
                  </HStack>
                )}
              </Flex>
            </Box>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th w="40px">
                      <Checkbox
                        isChecked={selectedInvoices.length === unpaidInvoices.length && unpaidInvoices.length > 0}
                        isIndeterminate={selectedInvoices.length > 0 && selectedInvoices.length < unpaidInvoices.length}
                        onChange={selectAllUnpaid}
                        colorScheme="green"
                      />
                    </Th>
                    <Th>Invoice</Th>
                    <Th>Batch</Th>
                    <Th>Date</Th>
                    <Th isNumeric>Draws</Th>
                    <Th isNumeric>Amount</Th>
                    <Th w="120px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {unpaidInvoices.map(inv => (
                    <Tr key={inv.id} _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => toggleInvoice(inv.id)}>
                      <Td onClick={e => e.stopPropagation()}>
                        <Checkbox
                          isChecked={selectedInvoices.includes(inv.id)}
                          onChange={() => toggleInvoice(inv.id)}
                          colorScheme="green"
                        />
                      </Td>
                      <Td>
                        <Text fontWeight="700" fontSize="sm">{inv.invoice_number}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" color="gray.500">{inv.batch_number || '—'}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" color="gray.600">{formatDate(inv.draw_date || inv.created_at)}</Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="xs" color="gray.600">{inv.draw_count}</Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontWeight="700" fontSize="sm" color="gray.800">{formatCurrency(inv.total_amount)}</Text>
                      </Td>
                      <Td onClick={e => e.stopPropagation()}>
                        <HStack spacing={1}>
                          <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FaEye />}
                            onClick={() => openInvoiceDetail(inv)}>
                            View
                          </Button>
                          <Button size="xs" variant="ghost" colorScheme="gray" leftIcon={<FaPrint />}
                            onClick={() => handlePrintInvoice(inv)}>
                            PDF
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Paid Invoices */}
        {paidInvoices.length > 0 && (
          <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
            <Box px={{ base: 4, md: 6 }} py={4} bg="green.50" borderBottom="1px solid" borderColor="green.100">
              <HStack spacing={2}>
                <Icon as={FaCheckCircle} color="green.500" />
                <Text fontSize="md" fontWeight="700" color="green.700">Paid Invoices</Text>
              </HStack>
            </Box>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Invoice</Th>
                    <Th>Batch</Th>
                    <Th>Date</Th>
                    <Th isNumeric>Draws</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Status</Th>
                    <Th w="120px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paidInvoices.map(inv => (
                    <Tr key={inv.id} _hover={{ bg: 'gray.50' }}>
                      <Td><Text fontWeight="600" fontSize="sm">{inv.invoice_number}</Text></Td>
                      <Td><Text fontSize="xs" color="gray.500">{inv.batch_number || '—'}</Text></Td>
                      <Td><Text fontSize="xs" color="gray.600">{formatDate(inv.draw_date || inv.created_at)}</Text></Td>
                      <Td isNumeric><Text fontSize="xs" color="gray.600">{inv.draw_count}</Text></Td>
                      <Td isNumeric><Text fontWeight="600" fontSize="sm" color="gray.700">{formatCurrency(inv.total_amount)}</Text></Td>
                      <Td><Badge colorScheme="green" borderRadius="full" px={2}>PAID</Badge></Td>
                      <Td>
                        <HStack spacing={1}>
                          <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FaEye />}
                            onClick={() => openInvoiceDetail(inv)}>
                            View
                          </Button>
                          <Button size="xs" variant="ghost" colorScheme="gray" leftIcon={<FaPrint />}
                            onClick={() => handlePrintInvoice(inv)}>
                            PDF
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}

        {invoices.length === 0 && (
          <Box bg="white" borderRadius="2xl" boxShadow="md" p={10} textAlign="center">
            <Icon as={FaFileInvoiceDollar} boxSize={12} color="gray.200" mb={4} />
            <Text fontSize="md" color="gray.500">No invoices found for this lab.</Text>
          </Box>
        )}

        {/* Footer */}
        <Flex justify="space-between" align="center" mt={6} flexWrap="wrap" gap={2}>
          <Text fontSize="xs" color="gray.500" fontWeight="600">AtHomeBloodDraw.com</Text>
          <Text fontSize="xs" color="gray.400">Questions? Contact support@athomeblooddraw.com</Text>
        </Flex>

        {/* ===== INVOICE DETAIL OVERLAY MODAL ===== */}
        <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl" scrollBehavior="inside">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden" maxH="90vh">
            {viewingInvoice && (
              <>
                {/* Header */}
                <Box bgGradient="linear(to-r, #276749, #38a169)" color="white" px={{ base: 6, md: 10 }} py={6} position="relative" overflow="hidden" flexShrink={0}>
                  <Box position="absolute" top={0} right={0} bottom={0} left={0} opacity={0.05}
                    bgImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)" bgSize="20px 20px" />
                  <Flex justify="space-between" align="flex-start" position="relative">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="800" letterSpacing="0.5px">AtHomeBloodDraw.com</Text>
                      <Heading size="lg" fontWeight="800" letterSpacing="-0.5px">INVOICE</Heading>
                      <Text fontSize="xs" opacity={0.7}>Mobile Phlebotomy Services</Text>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="xs" opacity={0.6} textTransform="uppercase" letterSpacing="wider">Invoice No.</Text>
                      <Text fontSize="xl" fontWeight="800" letterSpacing="1px">{viewingInvoice.invoice_number}</Text>
                      <Text fontSize="xs" opacity={0.6}>Issued {formatDate(viewingInvoice.created_at)}</Text>
                    </VStack>
                  </Flex>
                  <Box as="button" position="absolute" top={3} right={3} onClick={onViewClose}
                    color="whiteAlpha.800" _hover={{ color: 'white' }} p={2}>
                    <Icon as={FaTimes} boxSize={4} />
                  </Box>
                </Box>

                {/* Status Bar */}
                <Box
                  bg={isViewingPaid ? 'green.50' : 'orange.50'}
                  px={{ base: 6, md: 10 }} py={3}
                  borderBottom="1px solid" borderColor={isViewingPaid ? 'green.100' : 'orange.100'}
                  flexShrink={0}
                >
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Icon as={isViewingPaid ? FaCheckCircle : FaCreditCard} color={isViewingPaid ? 'green.500' : 'orange.500'} boxSize={4} />
                      <Text fontSize="sm" fontWeight="bold" color={isViewingPaid ? 'green.700' : 'orange.700'}>
                        {isViewingPaid ? 'Payment Received' : 'Payment Required'}
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      {viewingDetail && (
                        <Button size="xs" variant="ghost" colorScheme="gray" leftIcon={<FaPrint />} onClick={handlePrintFromModal}>
                          Print
                        </Button>
                      )}
                      <Text fontSize="xl" fontWeight="800" color={isViewingPaid ? 'green.700' : 'orange.700'}>
                        {formatCurrency(viewingInvoice.total_amount)}
                      </Text>
                    </HStack>
                  </Flex>
                </Box>

                <ModalBody px={0} py={0}>
                  {detailLoading ? (
                    <Flex justify="center" py={12}>
                      <Spinner size="lg" color="green.500" />
                    </Flex>
                  ) : viewingDetail?.line_items ? (
                    <>
                      {/* Bill To Section */}
                      <Box px={{ base: 6, md: 10 }} py={5}>
                        <SimpleGrid columns={2} spacing={4}>
                          <Box>
                            <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>Bill To</Text>
                            <Text fontSize="md" fontWeight="700" color="gray.800">{lab?.name || 'Lab'}</Text>
                            <Text fontSize="sm" color="gray.500" mt={0.5}>{lab?.email || ''}</Text>
                          </Box>
                          <Box textAlign="right">
                            <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>Status</Text>
                            <Badge
                              colorScheme={isViewingPaid ? 'green' : 'red'}
                              fontSize="sm" px={4} py={1.5} borderRadius="full" fontWeight="700"
                            >
                              {isViewingPaid ? 'PAID' : 'UNPAID'}
                            </Badge>
                          </Box>
                        </SimpleGrid>
                      </Box>

                      <Divider borderColor="gray.100" />

                      {/* Line Items */}
                      <Box px={{ base: 6, md: 10 }} py={5}>
                        <Grid
                          templateColumns={{ base: '1fr auto', md: '1fr 120px 90px' }}
                          gap={4} pb={3} mb={4}
                          borderBottom="2px solid" borderColor="gray.200"
                        >
                          <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider">Service Details</Text>
                          <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" display={{ base: 'none', md: 'block' }}>Date</Text>
                          <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" textAlign="right">Amount</Text>
                        </Grid>

                        <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                          {viewingDetail.line_items.map((item, i) => {
                            const itemTotal = (item.draw_fee || 0) + (item.admin_fee || 0) + (item.ship_fee || 0);
                            return (
                              <Grid
                                key={i}
                                templateColumns={{ base: '1fr auto', md: '1fr 120px 90px' }}
                                gap={4} py={3}
                                _hover={{ bg: 'gray.50' }}
                                mx={-3} px={3} borderRadius="lg"
                                transition="background 0.15s"
                              >
                                <GridItem>
                                  <HStack spacing={3} align="flex-start">
                                    <Box bg="green.50" borderRadius="lg" p={2} mt={0.5} flexShrink={0}>
                                      <Icon as={FaVial} color="green.400" boxSize={3} />
                                    </Box>
                                    <Box flex={1}>
                                      <Text fontSize="sm" fontWeight="700" color="gray.800">{getLastName(item.patient_name)}</Text>
                                      <HStack spacing={3} mt={1} flexWrap="wrap">
                                        {item.patient_dob && (
                                          <HStack spacing={1}>
                                            <Text fontSize="xs" color="gray.400">YOB:</Text>
                                            <Text fontSize="xs" color="gray.600" fontWeight="500">{getBirthYear(item.patient_dob)}</Text>
                                          </HStack>
                                        )}
                                      </HStack>
                                      {(item.doctor_name || item.phlebotomist_name) && (
                                        <HStack spacing={4} mt={1} flexWrap="wrap">
                                          {item.doctor_name && (
                                            <HStack spacing={1}>
                                              <Icon as={FaStethoscope} color="gray.300" boxSize={2.5} />
                                              <Text fontSize="xs" color="gray.500">{item.doctor_name}</Text>
                                            </HStack>
                                          )}
                                          {item.phlebotomist_name && (
                                            <HStack spacing={1}>
                                              <Icon as={FaSyringe} color="gray.300" boxSize={2.5} />
                                              <Text fontSize="xs" color="gray.500">{item.phlebotomist_name}</Text>
                                            </HStack>
                                          )}
                                        </HStack>
                                      )}
                                      {item.tests && item.tests.length > 0 && (
                                        <HStack spacing={1} mt={1.5} flexWrap="wrap">
                                          {item.tests.map((test, ti) => (
                                            <Badge
                                              key={ti} bg="green.50" color="green.600"
                                              fontSize="10px" fontWeight="600" px={2} py={0.5}
                                              borderRadius="full" border="1px solid" borderColor="green.100"
                                            >
                                              {test}
                                            </Badge>
                                          ))}
                                        </HStack>
                                      )}
                                      {item.diagnosis && (
                                        <HStack spacing={1} mt={1}>
                                          <Text fontSize="xs" color="gray.400" fontWeight="600">ICD:</Text>
                                          <Text fontSize="xs" color="gray.500" filter={redactPHI ? 'blur(5px)' : 'none'} userSelect={redactPHI ? 'none' : 'auto'}>{item.diagnosis}</Text>
                                        </HStack>
                                      )}
                                    </Box>
                                  </HStack>
                                </GridItem>
                                <GridItem display={{ base: 'none', md: 'flex' }} alignItems="center">
                                  <Text fontSize="sm" color="gray.600">{formatDate(item.collection_date)}</Text>
                                </GridItem>
                                <GridItem display="flex" alignItems="center" justifyContent="flex-end">
                                  <VStack spacing={0} align="end">
                                    <Text fontSize="sm" fontWeight="700" color="gray.800">{formatCurrency(itemTotal)}</Text>
                                    <Text fontSize="10px" color="gray.400">
                                      Draw {formatCurrency(item.draw_fee)} + Admin {formatCurrency(item.admin_fee)}
                                      {item.ship_fee > 0 ? ` + Ship ${formatCurrency(item.ship_fee)}` : ''}
                                    </Text>
                                  </VStack>
                                </GridItem>
                              </Grid>
                            );
                          })}
                        </VStack>
                      </Box>

                      {/* Summary Section */}
                      <Box bg="gray.50" px={{ base: 6, md: 10 }} py={4} borderTop="2px solid" borderColor="gray.200">
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold">Total Due</Text>
                            <Text fontSize="sm" color="gray.500">{viewingDetail.line_items.length} draws</Text>
                          </VStack>
                          <Text fontSize="2xl" fontWeight="800" color={isViewingPaid ? 'green.600' : 'gray.800'}>
                            {formatCurrency(viewingInvoice.total_amount)}
                          </Text>
                        </Flex>
                      </Box>

                      {/* Fee Breakdown */}
                      <Box px={{ base: 6, md: 10 }} py={4} borderTop="1px solid" borderColor="gray.100">
                        <SimpleGrid columns={3} spacing={4}>
                          <Box>
                            <Text fontSize="xs" color="gray.400" fontWeight="bold">Draw Fees</Text>
                            <Text fontSize="sm" fontWeight="600" color="gray.700">
                              {formatCurrency(viewingDetail.line_items.reduce((s, i) => s + (i.draw_fee || 0), 0))}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.400" fontWeight="bold">Admin Fees</Text>
                            <Text fontSize="sm" fontWeight="600" color="gray.700">
                              {formatCurrency(viewingDetail.line_items.reduce((s, i) => s + (i.admin_fee || 0), 0))}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.400" fontWeight="bold">Shipping</Text>
                            <Text fontSize="sm" fontWeight="600" color="gray.700">
                              {formatCurrency(viewingDetail.line_items.reduce((s, i) => s + (i.ship_fee || 0), 0))}
                            </Text>
                          </Box>
                        </SimpleGrid>
                      </Box>
                    </>
                  ) : (
                    <Box p={10} textAlign="center">
                      <Text color="gray.500">Could not load invoice details.</Text>
                    </Box>
                  )}
                </ModalBody>
              </>
            )}
          </ModalContent>
        </Modal>

        {/* Add Card Modal */}
        <Modal isOpen={isAddCardOpen} onClose={onAddCardClose} isCentered size="md" closeOnOverlayClick={false}>
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
            <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="lg" fontWeight="700">Add Card on File</Text>
                  <Text color="whiteAlpha.800" fontSize="sm">Save for future payments</Text>
                </VStack>
                <Box as="button" onClick={onAddCardClose} color="whiteAlpha.800" _hover={{ color: 'white' }} p={1}>
                  <Icon as={FaTimes} boxSize={4} />
                </Box>
              </Flex>
            </Box>
            <ModalBody px={6} py={5}>
              {setupClientSecret && stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret: setupClientSecret, appearance: stripeAppearance }}>
                  <SaveCardForm onSuccess={handleCardSaved} />
                </Elements>
              ) : (
                <Flex justify="center" py={8}><Spinner size="lg" color="green.500" /></Flex>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Pay Modal */}
        <Modal isOpen={isPayOpen} onClose={onPayClose} isCentered size="md" closeOnOverlayClick={false}>
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
            <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="lg" fontWeight="700">Payment</Text>
                  <Text color="whiteAlpha.800" fontSize="sm">{formatCurrency(selectedTotal)} — {selectedInvoices.length} invoice(s)</Text>
                </VStack>
                <Box as="button" onClick={onPayClose} color="whiteAlpha.800" _hover={{ color: 'white' }} p={1}>
                  <Icon as={FaTimes} boxSize={4} />
                </Box>
              </Flex>
            </Box>
            <ModalBody px={6} py={5}>
              {payClientSecret && stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret: payClientSecret, appearance: stripeAppearance }}>
                  <PayCardForm amount={selectedTotal} onSuccess={handlePaymentSuccess} />
                </Elements>
              ) : (
                <Flex justify="center" py={8}><Spinner size="lg" color="green.500" /></Flex>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Print Preview Overlay */}
        <Modal isOpen={isPrintOpen} onClose={onPrintClose} size="full" closeOnOverlayClick>
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="transparent" boxShadow="none" my={0} maxW="100vw">
            <ModalBody p={0} display="flex" flexDirection="column" alignItems="center" justifyContent="center" minH="100vh">
              <Flex w="100%" maxW="850px" justify="flex-end" mb={2} gap={3} px={4} pt={4}>
                <Button colorScheme="green" leftIcon={<FaPrint />} onClick={handlePrintFromOverlay} size="sm" fontWeight="600">
                  Print
                </Button>
                <Button variant="ghost" color="white" onClick={onPrintClose} size="sm" _hover={{ bg: 'whiteAlpha.200' }}>
                  <Icon as={FaTimes} />
                </Button>
              </Flex>
              <Box w="100%" maxW="850px" h="calc(100vh - 80px)" bg="white" borderRadius="md" overflow="hidden" mx="auto">
                <iframe
                  id="print-preview-iframe"
                  title="Print Preview"
                  srcDoc={printHtml || ''}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>

      </Container>
    </Box>
  );
}
